import img1 from "../../assets/Images/Event Booking/Availability popup.png"
import img2 from "../../assets/Images/Event Booking/Validation popup.png"

export default function AddBooking() {
  return (
    <section id="add-booking" className="space-y-16">

      <section className="bg-white rounded-xl border border-slate-200 p-10">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          2. Event Bookings
        </h1>
        <p className="text-md text-slate-600 max-w-3xl">
          Event Bookings allow you to officially confirm events, assign halls and
          slots, record customer details, and manage all financial aspects of an
          event from inquiry to completion.
        </p>


        {/* OVERVIEW */}
        <section className="bg-white border-l border-[#C9A24E] p-4 mt-2">
          <h2 className="text-lg font-semibold mb-3">
            Overview
          </h2>
          <p className="text-slate-600 text-sm">
            The Event Booking module is the core operational component of EventFlow.
            It ensures that each event is properly scheduled, financially recorded,
            and protected from conflicts. Bookings created here appear across the
            calendar, reports, receipts, and accounting modules.
          </p>
        </section>
      </section>

      {/* SECTION TITLE */}
      <div>
        <h2 className="text-xl font-semibold text-[#C9A24E] mb-3">
          2.1 Add Booking
        </h2>
        <p className="text-slate-600 max-w-3xl">
          This section provides a complete walkthrough of creating a new booking
          in EventFlow — from selecting dates and halls to calculating totals and
          saving the booking. <br />

          <p className="text-slate-600 mt-3">
            The Add Booking form is designed to ensure accuracy, prevent conflicts,
            and standardize bookings across the organization. Every field plays a
            role in maintaining financial and operational consistency.
          </p>
        </p>
      </div>
      

      {/* OVERVIEW */}
      {/* <section className="bg-white border border-slate-200 rounded-xl p-8">
        <h3 className="font-semibold mb-2">Overview</h3>
        <p className="text-slate-600">
          The Add Booking form is designed to ensure accuracy, prevent conflicts,
          and standardize bookings across the organization. Every field plays a
          role in maintaining financial and operational consistency.
        </p>
      </section> */}

      {/* STEP 1 */}
      <section className="">
        <div>
          <h3 className="font-semibold mb-2">
            Step 1: Booking Date & Function Date
          </h3>
          <p className="text-slate-600 mb-3">
            The booking date is auto-filled based on the current system date,
            while the function date is selected by the user.
          </p>

          <ul className="list-disc pl-5 text-slate-600 space-y-1">
            <li>Function day is auto-calculated</li>
            <li>Date-based availability check is triggered</li>
            <li>Reduces human error in day selection</li>
          </ul>
        </div>
      </section>

      {/* AVAILABILITY */}
      <section className="bg-white border border-slate-200 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Hall Availability Check
        </h3>

        <p className="text-slate-600 mb-4">
          Upon selecting the function date or slot, EventFlow checks hall
          availability and displays a popup indicating which halls are available
          for Morning and Evening slots.
        </p>

        <ul className="list-disc pl-5 text-slate-600 space-y-1 mb-4">
          <li>Availability is calculated in real time</li>
          <li>Booked halls are excluded automatically</li>
          <li>Staff cannot select unavailable halls</li>
        </ul>

        <div className=" rounded-lg p-4 flex justify-center items-center ">
          <img src={img1} className="w-[400px] h-auto"/>
        </div>
      </section>

      {/* STEP 2 */}
      <section className="">
        <div>
          <h3 className="font-semibold mb-2">
            Step 2: Slot & Hall Selection
          </h3>
          <p className="text-slate-600 mb-3">
            The Slot dropdown dynamically displays available options (Morning /
            Evening). Based on the slot, hall options become selectable.
          </p>

          <ul className="list-disc pl-5 text-slate-600 space-y-1">
            <li>Single or multiple halls can be selected</li>
            <li>Guest capacity limits are displayed</li>
            <li>Slot conflicts are prevented</li>
          </ul>
        </div>

      </section>

      {/* STEP 3 */}
      <section className="bg-slate-100 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Step 3: Organizer & Contact Details
        </h3>

        <p className="text-slate-600 mb-4">
          Accurate organizer information is critical for communication, billing,
          and legal documentation.
        </p>

        <ul className="list-disc pl-5 text-slate-600 space-y-1">
          <li>Function Organizer Name</li>
          <li>CNIC / Identification</li>
          <li>Address & Contact Numbers</li>
          <li>Office / Alternate Contact</li>
        </ul>
      </section>

      {/* STEP 4 */}
      <section className="bg-white border border-slate-200 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Step 4: Event & Service Details
        </h3>

        <p className="text-slate-600 mb-4">
          Event-specific details define the type and nature of the function and
          additional services required.
        </p>

        <ul className="list-disc pl-5 text-slate-600 space-y-1">
          <li>Occasion (Wedding, Mehndi, Valima, etc.)</li>
          <li>Function type (Mix / Partition)</li>
          <li>Sound system requirement</li>
          <li>Guest count</li>
        </ul>
      </section>

      {/* STEP 5 */}
      <section className="bg-slate-100 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Step 5: Financial Calculation
        </h3>

        <p className="text-slate-600 mb-4">
          EventFlow automatically calculates totals based on entered values to
          ensure transparent and accurate billing.
        </p>

        <ul className="list-disc pl-5 text-slate-600 space-y-1">
          <li>Banquet rent</li>
          <li>Optional taxes (Sales / Withholding)</li>
          <li>Additional services</li>
          <li>Advance payment</li>
          <li>Auto-calculated balance</li>
        </ul>
      </section>

      {/* VALIDATION */}
      <section className="bg-red-50 border border-red-200 rounded-xl p-8">
        <h3 className="font-semibold mb-2 text-red-700">
          Validation & Error Messages
        </h3>

        <p className="text-slate-600 mb-4">
          If any required field is missing, EventFlow prevents submission and
          displays a validation popup listing all missing or incorrect fields.
        </p>

        <div className=" rounded-lg p-4 flex text-center justify-center">
          <img src={img2} className="w-[400px] h-auto"/>
        </div>
      </section>

      {/* SAVE */}
      <section className="bg-white border border-slate-200 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Saving the Booking
        </h3>

        <p className="text-slate-600">
          Once all required information is correctly entered, the booking can be
          saved. The system assigns a booking number and records the event across
          all relevant modules.
        </p>

        <ul className="list-disc pl-5 text-slate-600 space-y-1 mt-3">
          <li>Booking is added to the calendar</li>
          <li>Financial records are updated</li>
          <li>Booking appears in reports & lists</li>
        </ul>
      </section>

      {/* WHY IMPORTANT */}
      <section className="bg-slate-100 rounded-xl p-8">
        <h3 className="font-semibold mb-2">
          Why This Process Matters
        </h3>

        <p className="text-slate-600 max-w-3xl">
          This structured booking flow eliminates double bookings, reduces
          disputes, ensures financial clarity, and creates a reliable historical
          record for future reference.
        </p>
      </section>

    </section>
  );
}
